import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, Eye, ExternalLink, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/Button';
import { BookCoverUploader } from '@/components/admin/BookCoverUploader';
import { VocabularyBookmarkUploader } from '@/components/admin/VocabularyBookmarkUploader';
import { BookActivitiesManager } from '@/components/admin/BookActivitiesManager';
import { AdminBookMetaForm } from '@/components/admin/AdminBookMetaForm';
import { QuizSidebarLink } from '@/components/admin/QuizSidebarLink';
import { BookPromptManager } from '@/components/admin/BookPromptManager';
import { PublishingCard } from '@/components/admin/PublishingCard';
import {
  AdminBookLabelMultiSelect,
  LABEL_BUCKET_BY_TYPE,
} from '@/components/admin/AdminBookLabelMultiSelect';
import { booksService } from '@/services/books.service';
import { ApiError } from '@/services/http';
import { LABEL_TYPES } from '@/constants/label-types';
import { formatRelativeTime } from '@/lib/relative-time';
import { cn } from '@/lib/cn';
import { useSessionStore } from '@/store/session.store';
import type { BookLabelsView, BookView } from '@/types/api';

/**
 * AdminEditBook — `/admin/books/:id`. Assembles all the Phase 7 components
 * into the layout described in DESIGN.md Artboard 4 + ADMIN_DASHBOARD.md
 * §"Book Editor":
 *
 *   Breadcrumb bar
 *     Back to Admin · "Editing · <title>" · Auto-saved N min ago · Preview · Save
 *   Two columns
 *     Left (2/3): BookCoverUploader → AdminBookMetaForm → BookPromptManager
 *     Right (1/3): PublishingCard → Labels & categories card → Activity log
 *
 * State strategy:
 *   - Single `book` state powers every child. Each mutation (cover, meta,
 *     status, label toggle, label create) returns an updated `BookView`
 *     which we hand back to `onBookChange`. Label mutations return
 *     `BookLabelsView` (no title/status) so we merge them into the current
 *     `book` before propagating.
 *   - `lastSavedAt` is bumped whenever a mutation succeeds so the breadcrumb
 *     can show "Auto-saved N min ago". It defaults to the book's own
 *     `updatedAt` on first load, so the indicator is meaningful even before
 *     the admin has done anything.
 *
 * Error + loading states match the spec: skeleton while loading, banner
 * with "Back to Admin" if the GET fails (e.g. 404 / 403).
 */
export function AdminEditBookPage() {
  const { id } = useParams<{ id: string }>();
  // The access token is cached in-memory by `ApiAuthBridge` but is NOT
  // persisted to localStorage. When this page mounts in a fresh tab (the
  // Admin dashboard opens it via `window.open`) the bridge's async
  // `getAccessTokenSilently` hasn't resolved yet, so a naive fetch on mount
  // would fire without an Authorization header and the server would 401
  // with "Unauthorized". Subscribing to the token and waiting for it before
  // firing the GET removes that race.
  const accessToken = useSessionStore((s) => s.accessToken);
  const [book, setBook] = useState<BookView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  // Synchronous embedding regen takes ~1–2s (OpenAI roundtrip); the
  // button is disabled and the label switches to "Regenerating…" while
  // we wait so the admin doesn't click twice and double-spend the call.
  const [regeneratingEmbed, setRegeneratingEmbed] = useState(false);
  // Re-render the "N min ago" text every 30s so it stays fresh without the
  // admin having to refresh. Relative-time formatting is cheap.
  const [tickNow, setTickNow] = useState(() => Date.now());

  useEffect(() => {
    if (!id) return;
    // Hold off until `ApiAuthBridge` has populated the token — see comment
    // on the `accessToken` selector above.
    if (!accessToken) return;
    let cancelled = false;
    setBook(null);
    setLoadError(null);
    (async () => {
      try {
        const fetched = await booksService.getById(id);
        if (cancelled) return;
        setBook(fetched);
        setLastSavedAt(fetched.updatedAt);
      } catch (err) {
        if (cancelled) return;
        const status = err instanceof ApiError ? err.status : undefined;
        setLoadError(
          status === 404
            ? 'We couldn\u2019t find that book. It may have been deleted.'
            : err instanceof ApiError
              ? err.message
              : 'Could not load the book.',
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, accessToken]);

  useEffect(() => {
    const interval = window.setInterval(() => setTickNow(Date.now()), 30_000);
    return () => window.clearInterval(interval);
  }, []);

  const handleBookChange = (updated: BookView) => {
    setBook(updated);
    setLastSavedAt(updated.updatedAt ?? new Date().toISOString());
  };

  /**
   * Synchronous embedding regen. Disabled while the call is in flight so
   * the admin can't fire two OpenAI calls in parallel. On success we
   * splat the refreshed BookView (which carries the new
   * `embeddingGeneratedAt`) back onto local state via `handleBookChange`
   * so the inline timestamp updates without a re-fetch.
   */
  const handleRegenerateEmbedding = async () => {
    if (!book || regeneratingEmbed) return;
    setRegeneratingEmbed(true);
    try {
      const refreshed = await booksService.regenerateEmbedding(book.id);
      handleBookChange(refreshed);
      toast.success('Embedding regenerated.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not regenerate the embedding.');
    } finally {
      setRegeneratingEmbed(false);
    }
  };

  // Label multi-selects return a BookLabelsView — no title/status/createdAt.
  // Merge it onto the current book so downstream consumers (cover, meta form)
  // don't see stale label data, and so the activity log's timestamps update.
  const handleLabelsChange = (labels: BookLabelsView) => {
    setBook((prev) =>
      prev
        ? {
            ...prev,
            bookSeries: labels.bookSeries,
            genres: labels.genres,
            topics: labels.topics,
            readingLevels: labels.readingLevels,
            interestLevels: labels.interestLevels,
            historicalEvents: labels.historicalEvents,
            timePeriods: labels.timePeriods,
          }
        : prev,
    );
    setLastSavedAt(new Date().toISOString());
  };

  const relativeSaved = useMemo(
    () => formatRelativeTime(lastSavedAt, new Date(tickNow)),
    [lastSavedAt, tickNow],
  );

  if (loadError) {
    return (
      <AppShell mode="parent">
        <div className="mx-auto max-w-xl rounded-2xl border border-or-rose-200 bg-or-rose-50 p-8 text-center">
          <h1 className="font-display text-[20px] font-bold text-or-rose-700">
            {'This book couldn\u2019t be opened'}
          </h1>
          <p className="mt-2 text-[13px] text-or-rose-700/80">{loadError}</p>
          <div className="mt-6 flex justify-center">
            <Link to="/admin">
              <Button variant="secondary" size="md">
                <ArrowLeft className="h-4 w-4" aria-hidden />
                Back to Admin
              </Button>
            </Link>
          </div>
        </div>
      </AppShell>
    );
  }

  if (!book) {
    return (
      <AppShell mode="parent">
        <EditorSkeleton />
      </AppShell>
    );
  }

  return (
    <AppShell mode="parent">
      {/* Breadcrumb / sub-header. Keeps the editor anchored to the admin
          dashboard and gives the admin the auto-save signal + Preview + Save. */}
      <div className="flex flex-col gap-3 border-b border-or-slate-200 pb-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-or-slate-500 hover:text-or-slate-800"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            Back to Admin
          </Link>
          <span className="text-or-slate-300" aria-hidden>
            /
          </span>
          <div className="min-w-0">
            <div className="or-kicker text-or-slate-500">Admin · Book editor</div>
            <h1 className="truncate font-display text-[20px] font-bold text-or-slate-900">
              Editing · {book.title || 'Untitled book'}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold',
              relativeSaved
                ? 'bg-or-green-100 text-or-green-700'
                : 'bg-or-slate-100 text-or-slate-500',
            )}
            aria-live="polite"
          >
            <Clock className="h-3 w-3" aria-hidden />
            {relativeSaved ? `Auto-saved ${relativeSaved}` : 'Not saved yet'}
          </span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => toast.info('Preview is coming soon.')}
            title="Preview how readers will see this book"
          >
            <Eye className="h-4 w-4" aria-hidden />
            Preview
          </Button>
        </div>
      </div>

      {/* Body — two-column editor. Collapses to single column on small screens. */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-6">
          <BookCoverUploader book={book} onBookChange={handleBookChange} />
          <VocabularyBookmarkUploader book={book} onBookChange={handleBookChange} />
          <AdminBookMetaForm book={book} onBookChange={handleBookChange} />
          <BookActivitiesManager bookId={book.id} />
          <BookPromptManager bookId={book.id} />
        </div>

        <aside className="flex flex-col gap-6">
          <PublishingCard book={book} onBookChange={handleBookChange} />

          <div className="rounded-2xl border border-or-slate-200 bg-white p-5 shadow-sm">
            <QuizSidebarLink parentKind="book" parentId={book.id} />
          </div>

          <div className="rounded-2xl border border-or-slate-200 bg-white p-5 shadow-sm">
            <div className="or-kicker text-or-slate-500">Labels & categories</div>
            <h2 className="mt-1 font-display text-[16px] font-bold text-or-slate-900">
              Applied to this book
            </h2>
            <p className="mt-1 text-[12px] leading-relaxed text-or-slate-500">
              Toggling a label saves instantly — no separate save step.
            </p>
            <div className="mt-3 divide-y divide-or-slate-100">
              {LABEL_TYPES.map((type) => (
                <AdminBookLabelMultiSelect
                  key={type}
                  bookId={book.id}
                  labelType={type}
                  labels={book[LABEL_BUCKET_BY_TYPE[type]]}
                  onLabelsChange={handleLabelsChange}
                />
              ))}
            </div>
          </div>

          {/* Activity log placeholder — we don't have an audit endpoint yet,
              so we stub it with the last save action derived from the same
              `lastSavedAt` used in the breadcrumb. Per CLIENT_PLAN this is
              acceptable until the API surfaces real entries. */}
          <div className="rounded-2xl border border-or-slate-200 bg-white p-5 shadow-sm">
            <div className="or-kicker text-or-slate-500">Activity log</div>
            <h2 className="mt-1 font-display text-[16px] font-bold text-or-slate-900">
              Recent changes
            </h2>
            <ul className="mt-3 flex flex-col gap-3">
              <li className="flex gap-3 text-[12px] text-or-slate-600">
                <span aria-hidden className="mt-1 h-2 w-2 shrink-0 rounded-full bg-or-orange-400" />
                <div>
                  <p className="font-semibold text-or-slate-800">Last save</p>
                  <p className="text-or-slate-500">
                    {relativeSaved
                      ? `Saved ${relativeSaved}.`
                      : 'No changes saved in this session yet.'}
                  </p>
                </div>
              </li>
              <li className="flex gap-3 text-[12px] text-or-slate-600">
                <span aria-hidden className="mt-1 h-2 w-2 shrink-0 rounded-full bg-or-slate-300" />
                <div>
                  <p className="font-semibold text-or-slate-800">Created</p>
                  <p className="text-or-slate-500">
                    {formatRelativeTime(book.createdAt, new Date(tickNow)) || 'Unknown'}
                  </p>
                </div>
              </li>
              {book.bookCover ? (
                <li className="flex items-center gap-2 text-[12px]">
                  <a
                    href={book.bookCover}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-or-orange-600 hover:text-or-orange-700"
                  >
                    <ExternalLink className="h-3 w-3" aria-hidden />
                    Open cover file
                  </a>
                </li>
              ) : null}
            </ul>
          </div>
        </aside>
      </div>

      {/* --- Bottom action bar: embedding regen ---
          Pinned below the two-column editor so it doesn't fight with the
          per-section "Save" / label-toggle affordances above. The button
          fires a synchronous OpenAI call (~1–2s); the date label next to
          it updates inline from the API response so the admin can see
          the embed actually fired. */}
      <div className="mt-8 flex flex-col gap-3 rounded-2xl border border-or-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="or-kicker text-or-slate-500">Search index</div>
          <h2 className="mt-1 font-display text-[16px] font-bold text-or-slate-900">
            Embedding
          </h2>
          <p className="mt-1 text-[12px] leading-relaxed text-or-slate-500">
            {book.embeddingGeneratedAt
              ? `Last embed generated ${
                  formatRelativeTime(book.embeddingGeneratedAt, new Date(tickNow)) ?? 'recently'
                } · ${new Date(book.embeddingGeneratedAt).toLocaleString()}`
              : 'This book has never been embedded — readers will not see it in semantic search results until you run a regen.'}
          </p>
        </div>
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={() => void handleRegenerateEmbedding()}
          disabled={regeneratingEmbed}
          title="Regenerate the book's OpenAI embedding from its title, author, and description"
        >
          <Sparkles className="h-4 w-4" aria-hidden />
          {regeneratingEmbed ? 'Regenerating…' : 'Regen Embeds'}
        </Button>
      </div>
    </AppShell>
  );
}

/**
 * Skeleton mirroring the editor's two-column layout so the first paint has
 * roughly the right shape while the GET is in flight.
 */
function EditorSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="flex items-center justify-between border-b border-or-slate-200 pb-4">
        <div className="h-5 w-60 rounded-full bg-or-slate-200" />
        <div className="h-8 w-40 rounded-full bg-or-slate-200" />
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-6">
          <div className="h-[270px] rounded-2xl bg-or-slate-100" />
          <div className="h-[360px] rounded-2xl bg-or-slate-100" />
          <div className="h-[220px] rounded-2xl bg-or-slate-100" />
        </div>
        <div className="flex flex-col gap-6">
          <div className="h-[180px] rounded-2xl bg-or-slate-100" />
          <div className="h-[420px] rounded-2xl bg-or-slate-100" />
          <div className="h-[160px] rounded-2xl bg-or-slate-100" />
        </div>
      </div>
    </div>
  );
}
